import {ExcludeCondition} from 'graphql-client/declarative-translations/RESTExcludeConditions';

import BaseFieldTranslator from './BaseFieldTranslator';
import {DeclarativeTranslator, TranslatableValue} from './DeclarativeTranslator';
import {PossibleTypesMapping} from './PossibleTypesMapping';
import {GraphQLSelectionSet, RESTField} from './preprocessedData';
import translateListOrObject from './translateListOrObject';

/**
 * Translator that handles nested shapes.
 *
 * Can be called recursively to handle multiple levels of nesting.
 *
 * Examples:
 *    - x { y, z } <-> x { y, z }
 *    - x { y { z } } <-> x { y { z } }
 */
export default class NestedFieldTranslator extends BaseFieldTranslator {
  declarativeTranslator: DeclarativeTranslator;

  constructor(
    restKey: string,
    graphQLKey: string,
    excludeCondition: ExcludeCondition | null,
    declarativeTranslator: DeclarativeTranslator
  ) {
    super(restKey, graphQLKey, excludeCondition);
    this.declarativeTranslator = declarativeTranslator;
  }

  public static tryParse(
    declarationName: string,
    parsedRESTField: RESTField,
    graphQLSelectionSet: GraphQLSelectionSet,
    possibleTypesMapping: PossibleTypesMapping
  ): NestedFieldTranslator | null {
    if (parsedRESTField.selectionSet) {
      const gqlField = parsedRESTField.customGraphQLName || parsedRESTField.fieldName;

      const nestedGraphQLSelectionSet = GraphQLSelectionSet.create(
        graphQLSelectionSet.fieldsByName[gqlField],
        graphQLSelectionSet.declarativeTranslationsMapping
      );

      const translator = DeclarativeTranslator.build(
        `${declarationName}:${parsedRESTField.fieldName}`,
        parsedRESTField.selectionSet,
        nestedGraphQLSelectionSet,
        possibleTypesMapping
      );

      return new NestedFieldTranslator(
        parsedRESTField.fieldName,
        gqlField,
        parsedRESTField.getExcludeCondition(),
        translator
      );
    }

    return null;
  }

  public getGQLValue(restObj: TranslatableValue): unknown {
    const restValue = restObj[this.restKey];
    return translateListOrObject(restValue, (value: TranslatableValue) =>
      this.declarativeTranslator.restToGQL(value)
    );
  }

  public getRESTValue(gqlObj: TranslatableValue): unknown {
    const gqlValue = gqlObj[this.graphQLKey];
    return translateListOrObject(gqlValue, (value: TranslatableValue) =>
      this.declarativeTranslator.gqlToREST(value)
    );
  }
}
